import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Check, X, Eye, ArrowUpCircle, Clock, MessageSquare } from "lucide-react";
import { PageHeader, Badge, EmptyState } from "@/components/Common";
import { useAuth } from "@/contexts/AuthContext";
import PolicyPreview from "@/components/PolicyPreview";

const TYPES = ["purchase", "leave", "expense", "transfer", "hiring", "admin_request", "other"];

// Ordered actions with visual metadata
export const ACTIONS = [
  { key: "approve",    label: "Approve",   verb: "approved",   Icon: Check,          tone: "emerald", btnClass: "bg-emerald-900 hover:bg-emerald-950 text-white" },
  { key: "on_review",  label: "On Review", verb: "on review",  Icon: Eye,            tone: "blue",    btnClass: "border-blue-700 text-blue-700 hover:bg-blue-50", variant: "outline" },
  { key: "escalate",   label: "Escalate",  verb: "escalated",  Icon: ArrowUpCircle,  tone: "amber",   btnClass: "border-amber-700 text-amber-700 hover:bg-amber-50", variant: "outline" },
  { key: "pending",    label: "Pending",   verb: "pending",    Icon: Clock,          tone: "slate",   btnClass: "border-gray-400 text-gray-700 hover:bg-gray-100", variant: "outline" },
  { key: "reject",     label: "Reject",    verb: "rejected",   Icon: X,              tone: "red",     btnClass: "border-red-700 text-red-700 hover:bg-red-50", variant: "outline" },
];

export const STATUS_TONE = {
  approved: "emerald",
  pending: "amber",
  on_review: "blue",
  rejected: "red",
  escalated: "amber",
};

const STATUS_LABEL = {
  approved: "Approved",
  pending: "Pending",
  on_review: "On Review",
  rejected: "Rejected",
  escalated: "Escalated",
};

function ActionDialog({ open, onClose, action, onSubmit }) {
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setComment(""); }, [open]);
  if (!action) return null;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await onSubmit(action.key, comment); onClose(); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <action.Icon size={16} /> Mark as {action.label}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="approval-action-form">
          <div>
            <Label className="flex items-center gap-1.5"><MessageSquare size={12} /> Description / Comment</Label>
            <Textarea
              rows={4} value={comment} onChange={(e) => setComment(e.target.value)}
              placeholder={
                action.key === "reject" ? "Reason for rejection…"
                : action.key === "escalate" ? "Why is this being escalated?"
                : action.key === "on_review" ? "What still needs to be reviewed?"
                : "Optional note (visible to requester and other approvers)"
              }
              className="mt-1 resize-none"
              data-testid="approval-action-comment"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy} className={action.btnClass}
              data-testid={`approval-action-submit-${action.key}`}>
              {busy ? "Saving…" : `Confirm ${action.label}`}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function HistoryList({ history }) {
  if (!history?.length) return null;
  return (
    <div className="mt-3 border-t border-gray-100 pt-3 space-y-2">
      <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Decision trail</div>
      {history.map((h, i) => {
        const meta = ACTIONS.find((a) => a.key === h.action);
        return (
          <div key={i} className="flex gap-2 text-xs">
            <Badge tone={meta?.tone || "slate"}>{meta?.verb || h.action}</Badge>
            <div className="flex-1 min-w-0">
              <div className="text-gray-800"><b>{h.user_name}</b> <span className="text-gray-500">· {new Date(h.at).toLocaleString()}</span></div>
              {h.comment && <div className="text-gray-600 mt-0.5">{h.comment}</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function Approvals() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [approverSlots, setApproverSlots] = useState([{ id: "s1" }, { id: "s2" }]);
  const [form, setForm] = useState({
    title: "", type: "purchase", amount: 0, description: "", branch: "",
    approvers: ["", ""],
  });
  const [actionCtx, setActionCtx] = useState(null); // { approvalId, action }

  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [{ data: a }, { data: u }] = await Promise.all([api.get("/approvals"), api.get("/users")]);
        if (!cancelled) { setItems(a); setUsers(u); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshKey]);
  const load = () => setRefreshKey((k) => k + 1);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/approvals", {
        ...form,
        amount: parseFloat(form.amount) || 0,
        approvers: form.approvers.filter(Boolean),
      });
      toast.success("Request submitted");
      setOpen(false);
      setForm({ title: "", type: "purchase", amount: 0, description: "", branch: "", approvers: ["", ""] });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const performAction = async (action, comment) => {
    if (!actionCtx) return;
    try {
      await api.post(`/approvals/${actionCtx.approvalId}/action`, { action, comment });
      toast.success(`Marked as ${STATUS_LABEL[action] || action}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Administration"
        title="Approval Workflows"
        subtitle="Multi-step approvals with rich statuses (Approved, Pending, On Review, Rejected, Escalated) and comments on every decision."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-900 hover:bg-emerald-950 text-white" data-testid="approval-new-button">
                <Plus size={16} className="mr-1.5" /> New request
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="font-display">Approval request</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-3" data-testid="approval-form">
                <div>
                  <Label>Title</Label>
                  <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className="mt-1" data-testid="approval-title-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Type</Label>
                    <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                      <SelectTrigger className="mt-1" data-testid="approval-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {TYPES.map((t) => (<SelectItem key={t} value={t}>{t.replaceAll("_", " ")}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Amount (UGX)</Label>
                    <Input type="number" value={form.amount}
                      onChange={(e) => setForm({ ...form, amount: e.target.value })}
                      className="mt-1" />
                  </div>
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea rows={3} value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="mt-1" />
                </div>
                <div className="space-y-2">
                  <Label>Approval chain (in order)</Label>
                  <PolicyPreview
                    type={form.type}
                    amount={form.amount}
                    branch={form.branch}
                    onResolved={(d) => {
                      if (!d?.resolved) return;
                      const ids = d.approvers || [];
                      // Auto-populate approver slots if empty
                      const hasAny = form.approvers.some(Boolean);
                      if (!hasAny && ids.length > 0) {
                        setApproverSlots(ids.map((_, i) => ({ id: `p${i}` })));
                        setForm((f) => ({ ...f, approvers: ids }));
                      }
                    }}
                  />
                  {form.approvers.map((aid, i) => (
                    <Select key={approverSlots[i]?.id || `slot-${i}`} value={aid}
                      onValueChange={(v) => {
                        const next = [...form.approvers];
                        next[i] = v;
                        setForm({ ...form, approvers: next });
                      }}>
                      <SelectTrigger data-testid={`approval-approver-${i}-select`}>
                        <SelectValue placeholder={`Approver ${i + 1}`} />
                      </SelectTrigger>
                      <SelectContent>
                        {users.filter((u) => u.id !== user?.id).map((u) => (
                          <SelectItem key={u.id} value={u.id}>{u.name} · {u.role?.replaceAll("_", " ")}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ))}
                  <Button type="button" variant="ghost" size="sm"
                    onClick={() => {
                      setApproverSlots([...approverSlots, { id: `s${Date.now()}` }]);
                      setForm({ ...form, approvers: [...form.approvers, ""] });
                    }}>
                    + Add approver
                  </Button>
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={busy}
                    className="bg-emerald-900 hover:bg-emerald-950 text-white"
                    data-testid="approval-submit-button">
                    {busy ? "Submitting…" : "Submit"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <ApprovalsList
        loading={loading} items={items} user={user}
        onAct={(approvalId, actionMeta) => setActionCtx({ approvalId, action: actionMeta })}
      />

      <ActionDialog
        open={!!actionCtx} action={actionCtx?.action}
        onClose={() => setActionCtx(null)} onSubmit={performAction}
      />
    </div>
  );
}

/** Reusable list — also consumed by AdminRequests page. */
export function ApprovalsList({ loading, items, user, onAct }) {
  if (loading) return <div className="p-10 text-center text-sm text-gray-500">Loading…</div>;
  if (items.length === 0) return <EmptyState title="No requests yet" hint="Create the first request above." />;
  return (
    <div className="space-y-3" data-testid="approvals-list">
      {items.map((a) => {
        const step = a.current_step || 0;
        const isFinal = a.status === "approved" || a.status === "rejected";
        const isCurrentApprover = (a.approvers || [])[step] === user?.id;
        const isEscalated = a.status === "escalated";
        const isSenior = ["managing_director", "executive_director", "general_manager"].includes(user?.role);
        const inChain = (a.approvers || []).includes(user?.id);
        const canAct = !isFinal && (isCurrentApprover || (isEscalated && (inChain || isSenior)));
        return (
          <div key={a.id} className="bg-white border border-gray-200 rounded-md p-5"
            data-testid={`approval-row-${a.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <Badge tone={STATUS_TONE[a.status] || "slate"}>{STATUS_LABEL[a.status] || a.status}</Badge>
                  <Badge tone="slate">{(a.type || "").replaceAll("_", " ")}</Badge>
                  {a.amount > 0 && <span className="text-xs text-gray-600">UGX {Number(a.amount).toLocaleString()}</span>}
                </div>
                <div className="font-display font-semibold text-gray-900">{a.title}</div>
                {a.description && <div className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{a.description}</div>}
                <div className="text-xs text-gray-500 mt-2">
                  Requested by {a.created_by_name} · {new Date(a.created_at).toLocaleString()}
                </div>
                <div className="flex flex-wrap gap-1.5 mt-2 text-[10px] uppercase tracking-wider">
                  {(a.approver_names || []).map((n, i) => (
                    <span key={`${a.id}-approver-${i}`}
                      className={`px-2 py-0.5 rounded-full border ${
                        i < step ? "bg-emerald-50 border-emerald-200 text-emerald-800" :
                        i === step ? "bg-amber-50 border-amber-200 text-amber-800" :
                        "bg-gray-50 border-gray-200 text-gray-500"
                      }`}>
                      {i + 1}. {n}
                    </span>
                  ))}
                </div>
                <HistoryList history={a.history} />
              </div>
              {canAct && (
                <div className="flex flex-wrap gap-2 flex-shrink-0">
                  {ACTIONS.map((act) => (
                    <Button key={act.key} size="sm" variant={act.variant}
                      onClick={() => onAct(a.id, act)}
                      className={act.btnClass}
                      data-testid={`approval-${act.key}-${a.id}`}>
                      <act.Icon size={14} className="mr-1" /> {act.label}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
